/**
 * Localization system for Yuklar Web UI
 * Supports: EN, RU, TR, UZ_LAT, UZ_CYR
 */

export type AppLanguage = "EN" | "RU" | "TR" | "UZ_LAT" | "UZ_CYR";
export type IntlLanguage = "en" | "ru" | "tr" | "uz-Latn" | "uz-Cyrl";

export const LANGUAGE_KEY = "yuklar_language";
export const DEFAULT_LANGUAGE: AppLanguage = "EN";
export const ALL_LANGUAGES: AppLanguage[] = [
  "EN",
  "RU",
  "TR",
  "UZ_LAT",
  "UZ_CYR",
];
export const ALL_INTL_LANGUAGES: IntlLanguage[] = [
  "en",
  "ru",
  "tr",
  "uz-Latn",
  "uz-Cyrl",
];

export const languageNames: Record<AppLanguage, string> = {
  EN: "English",
  RU: "Русский",
  TR: "Türkçe",
  UZ_LAT: "O'zbekcha",
  UZ_CYR: "Ўзбекча",
};

export const languageFlags: Record<AppLanguage, string> = {
  EN: "🇺🇸",
  RU: "🇷🇺",
  TR: "🇹🇷",
  UZ_LAT: "🇺🇿",
  UZ_CYR: "🇺🇿",
};

const appLanguageToIntlLocale: Record<AppLanguage, IntlLanguage> = {
  EN: "en",
  RU: "ru",
  TR: "tr",
  UZ_LAT: "uz-Latn",
  UZ_CYR: "uz-Cyrl",
};

const intlLocaleToAppLanguage: Record<IntlLanguage, AppLanguage> = {
  en: "EN",
  ru: "RU",
  tr: "TR",
  "uz-Latn": "UZ_LAT",
  "uz-Cyrl": "UZ_CYR",
};

const localeMap: Record<AppLanguage, string> = {
  EN: "en-US",
  RU: "ru-RU",
  TR: "tr-TR",
  UZ_LAT: "uz-Latn-UZ",
  UZ_CYR: "uz-Cyrl-UZ",
};

export const isValidLanguage = (value: string): value is AppLanguage => {
  return ALL_LANGUAGES.includes(value as AppLanguage);
};

export const isValidIntlLanguage = (value: string): value is IntlLanguage => {
  return ALL_INTL_LANGUAGES.includes(value as IntlLanguage);
};

export const toIntlLanguage = (language: AppLanguage): IntlLanguage => {
  return appLanguageToIntlLocale[language];
};

export const fromIntlLanguage = (locale: string): AppLanguage => {
  if (isValidIntlLanguage(locale)) {
    return intlLocaleToAppLanguage[locale];
  }

  const normalized = locale.toLowerCase();
  if (normalized.startsWith("ru")) return "RU";
  if (normalized.startsWith("tr")) return "TR";
  if (normalized.startsWith("uz-cyrl")) return "UZ_CYR";
  if (normalized.startsWith("uz-latn")) return "UZ_LAT";
  if (normalized.startsWith("uz")) return "UZ_LAT";
  if (normalized.startsWith("en")) return "EN";

  return DEFAULT_LANGUAGE;
};

export const getSystemLanguage = (language: AppLanguage): string => {
  return localeMap[language];
};

const getLanguageFromCookieString = (
  cookieString: string | undefined,
): AppLanguage | null => {
  if (!cookieString) return null;

  const match = cookieString.match(
    new RegExp(`(?:^|;\\s*)${LANGUAGE_KEY}=([^;]+)`),
  );
  const value = match?.[1];

  return value && isValidLanguage(value) ? value : null;
};

export const getLanguageFromStorage = (): AppLanguage | null => {
  if (typeof window === "undefined") return null;

  const stored = localStorage.getItem(LANGUAGE_KEY);
  return stored && isValidLanguage(stored) ? stored : null;
};

export const getLanguageFromCookie = (): AppLanguage | null => {
  if (typeof window === "undefined") return null;
  return getLanguageFromCookieString(document.cookie);
};

const getLanguageFromNavigator = (): AppLanguage => {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;

  return fromIntlLanguage(navigator.language);
};

/**
 * Get current language from browser storage, cookie, or browser preference
 */
export const getLanguage = (): AppLanguage => {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  return (
    getLanguageFromStorage() ||
    getLanguageFromCookie() ||
    getLanguageFromNavigator()
  );
};

export const getLanguageFromAcceptLanguage = (
  header: string | null,
): AppLanguage => {
  if (!header) return DEFAULT_LANGUAGE;

  const candidates = header
    .split(",")
    .map((part) => part.split(";")[0]?.trim())
    .filter(Boolean) as string[];

  for (const candidate of candidates) {
    const language = fromIntlLanguage(candidate);
    if (
      language !== DEFAULT_LANGUAGE ||
      candidate.toLowerCase().startsWith("en")
    ) {
      return language;
    }
  }

  return DEFAULT_LANGUAGE;
};

/**
 * Set language in localStorage, cookie, and server-side user preference
 */
export const setLanguage = (
  lang: AppLanguage,
  options?: { skipServer?: boolean },
): void => {
  if (typeof window !== "undefined") {
    localStorage.setItem(LANGUAGE_KEY, lang);
    document.cookie = `${LANGUAGE_KEY}=${lang}; Path=/; Max-Age=31536000; SameSite=Lax`;

    void import("@/src/lib/i18n").then(({ i18n }) =>
      i18n.changeLanguage(toIntlLanguage(lang)),
    );

    if (!options?.skipServer) {
      syncLanguageToServer(lang);
    }
  }
};

/** Fire-and-forget sync of language preference to the backend */
// Direct fetch to avoid circular dep with lib/api/users → client → config.
const syncLanguageToServer = (lang: AppLanguage): void => {
  const token = localStorage.getItem("yuklar_access_token");
  if (!token) return;

  import("@/lib/runtime-config").then(({ loadRuntimeConfig }) =>
    loadRuntimeConfig().then(({ apiBaseUrl }) =>
      fetch(`${apiBaseUrl}/users/settings/language/${lang}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {}),
    ),
  );
};

export const getRequestLanguage = (options: {
  cookieValue?: string | undefined;
  acceptLanguage?: string | null;
}): AppLanguage => {
  if (options.cookieValue && isValidLanguage(options.cookieValue)) {
    return options.cookieValue;
  }

  return getLanguageFromAcceptLanguage(options.acceptLanguage ?? null);
};
